import { useContext, useState, useCallback, useEffect } from 'react';
import { PostContext } from '../context/PostContext';
import { getPostComments } from '../api/posts';

/**
 * Custom hook to access post context with accurate comment counts
 */
const usePost = () => {
  const context = useContext(PostContext);
  
  if (!context) {
    throw new Error('usePost must be used within a PostProvider');
  }

  const [commentCounts, setCommentCounts] = useState({});
  const [loadingComments, setLoadingComments] = useState(true);
  
  // When posts change, reset loading state to indicate we need fresh comment counts
  useEffect(() => {
    if (context.communityPosts && context.communityPosts.length > 0) {
      setLoadingComments(true);
    }
  }, [context.communityPosts]);
  
  /**
   * Fetch all comment counts in one go
   */
  const fetchAllCommentCounts = useCallback(async () => {
    // Don't do anything if there are no posts
    if (!context.communityPosts || context.communityPosts.length === 0) {
      setLoadingComments(false);
      return;
    }
    
    setLoadingComments(true);
    
    try {
      // Get post IDs
      const postIds = context.communityPosts.map(post => post._id);
      
      // Create batch of promises to fetch all comment counts in parallel
      const countPromises = postIds.map(async (postId) => {
        try {
          const response = await getPostComments(postId, { page: 1, limit: 1, parentOnly: true });
          return { 
            postId, 
            count: response?.data?.pagination?.total || 0
          };
        } catch (err) {
          console.error(`Error fetching count for post ${postId}:`, err);
          return { postId, count: 0 };
        }
      });
      
      // Wait for all promises to resolve
      const results = await Promise.all(countPromises);
      
      // Create a new comment counts object
      const newCounts = {};
      results.forEach(result => {
        newCounts[result.postId] = result.count;
      });
      
      setCommentCounts(newCounts);
    } catch (error) {
      console.error("Error fetching comment counts:", error);
    } finally {
      setLoadingComments(false);
    }
  }, [context.communityPosts]);
  
  // Update comment count for a specific post (e.g. after adding/removing comment)
  const updateCommentCount = useCallback((postId, count) => {
    setCommentCounts(prev => ({
      ...prev,
      [postId]: count
    }));
  }, []);
  
  return {
    ...context,
    commentCounts,
    loadingComments,
    fetchAllCommentCounts,
    updateCommentCount
  };
};

export default usePost;